import React, { useState, useEffect, useRef } from 'react';
import { getAccountsByUserId } from 'services/accounts/accountService';
import AccountDefaultImage from "assets/images/avatars/default-avatar.jpg";
import { toast } from 'sonner';
import { useNavigate, useLocation } from 'react-router-dom';
import api from 'config/api.config';
import { Instagram, Plus, Lock, RotateCcw, Menu } from 'lucide-react';
import { useInstagramConnection } from 'contexts/InstagramConnectionContext';
import AddAccountOverlay from 'components/modals/AddAccountOverlay';
import Spinner from 'components/common/Spinner';
import GuideTooltip from 'components/common/GuideTooltip';
import { ReactComponent as Logomark } from 'assets/images/logos/Logomark.svg';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'contexts/ThemeContext';
import { useSidebar } from 'contexts/SidebarContext';
import { logger } from 'utils/logger';
import { getErrorMessage } from 'utils/errors';

interface AccountItem {
  _id: string;
  username: string;
  name?: string;
  imgSrc?: string;
  profile_picture_base64?: string;
  is_active?: boolean;
  is_locked?: boolean;
  connection_status?: string;
  freeze_until?: string;
  followers_count?: number;
  following_count?: number;
  media_count?: number;
  // Backend returns additional dynamic fields; allow them.
  [key: string]: unknown;
}

interface SubscriptionInfo {
  daysRemaining?: number;
  planName?: string;
  accountsPermitted?: number;
  [key: string]: unknown;
}

interface AccountsApiResponse {
  status: number;
  data: {
    accounts?: AccountItem[];
    subscription?: SubscriptionInfo;
  } | AccountItem[];
}

const Accounts: React.FC = () => {
  const { t } = useTranslation();
  const [account, setAccounts] = useState<AccountItem[] | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [showViewAccountGuide, setShowViewAccountGuide] = useState<boolean>(false);
  const [reauthUsername, setReauthUsername] = useState<string | undefined>(undefined);
  const { isDark } = useTheme();
  const { toggleSidebar } = useSidebar();
  const viewAccountButtonRef = useRef<HTMLButtonElement>(null);
  const { connectionState, resumeConnection, setErrorState } = useInstagramConnection();

  const location = useLocation();
  const navigate = useNavigate();


  // Check if we should show the guide or auto-open overlay
  useEffect(() => {
    const state = location.state as { showViewAccountGuide?: boolean; autoOpenOverlay?: boolean; reauthUsername?: string };
    if (state?.showViewAccountGuide) {
      setShowViewAccountGuide(true);
      navigate(location.pathname, { replace: true, state: {} });

      setTimeout(() => {
        if (viewAccountButtonRef.current) {
          viewAccountButtonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 500);
    }
    if (state?.autoOpenOverlay) {
      if (state?.reauthUsername) {
        setReauthUsername(state.reauthUsername);
      }
      setModalOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const openModal = (username?: string) => {
    setReauthUsername(username);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setReauthUsername(undefined);
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleInstagramAuth = async () => {
    try {
      setLoading(true);
      const response = await api.get<{ redirectUrl: string }>('/api/instagram/auth-endpoint');
      window.location.href = response.data.redirectUrl;
    } catch (error: unknown) {
      logger.error('Instagram auth error:', error);
      toast.error(t('accounts.errors.authFailed'));
      setError(t('accounts.errors.connectFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = (await getAccountsByUserId()) as AccountsApiResponse;
        if (data.status !== 200) {
          throw new Error(`Unexpected response status: ${data.status}`);
        }
        // The API response structure is { accounts: [...], subscription: {...} }
        // So we need to access data.data.accounts instead of data.data
        const dataBody = data.data;
        const accountsData: AccountItem[] = Array.isArray(dataBody)
          ? dataBody
          : Array.isArray(dataBody?.accounts)
            ? dataBody.accounts
            : [];
        setAccounts(accountsData);

        // Store subscription data
        if (!Array.isArray(dataBody) && dataBody?.subscription) {
          setSubscription(dataBody.subscription);
        }

        // Auto-resume connection state for accounts based on their API status
        // Only if we're not already actively tracking a connection
        if (connectionState.step === 'idle') {
          const connectingAccount = accountsData.find(
            (acc) => acc.connection_status === 'connecting'
          );
          const errorAccount = accountsData.find(
            (acc) => acc.connection_status === 'connection_issue' || acc.connection_status === 'error' || acc.connection_status === 'failed'
          );

          if (connectingAccount) {
            // Re-establish WebSocket to listen for status updates
            resumeConnection(connectingAccount.username);
          } else if (errorAccount) {
            // Show error banner without opening WebSocket
            setErrorState(errorAccount.username);
          }
        }
      } catch (error: unknown) {
        logger.error('Account fetch error:', error);
        const response = (error as { response?: { status?: number; data?: { message?: string } } })?.response;
        if (response) {
          if (response.status === 401) {
            toast.error(t('accounts.errors.sessionExpired'));
            navigate('/login');
            return;
          }
          setError(response.data?.message || t('accounts.errors.fetchFailed'));
        } else {
          setError(t('accounts.errors.networkError'));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchAccounts();
  }, []);

  // Silently refresh accounts when WebSocket reports "Setting up your device"
  // or when connection reaches a terminal state (error/success) so the updated
  // connection_status from the API is reflected in the account cards
  useEffect(() => {
    const shouldRefresh =
      connectionState.status === 'Setting up your device' ||
      connectionState.step === 'error' ||
      connectionState.step === 'success';

    if (shouldRefresh) {
      // Small delay to allow the update-connection-status API to complete first
      const timeout = setTimeout(async () => {
        try {
          const data = (await getAccountsByUserId()) as AccountsApiResponse;
          if (data.status === 200) {
            const dataBody = data.data;
            const accountsData: AccountItem[] = Array.isArray(dataBody)
              ? dataBody
              : Array.isArray(dataBody?.accounts)
                ? dataBody.accounts
                : [];
            setAccounts(accountsData);
            if (!Array.isArray(dataBody) && dataBody?.subscription) {
              setSubscription(dataBody.subscription);
            }
          }
        } catch (err) {
          // Silent refresh — don't show errors
        }
      }, connectionState.step === 'error' || connectionState.step === 'success' ? 1500 : 0);

      return () => clearTimeout(timeout);
    }
  }, [connectionState.status, connectionState.step]);

  // Determine account status for tag display
  const getAccountStatus = (acc: AccountItem) => {
    // Check if this account is currently being connected via WebSocket
    if (
      connectionState.username === acc.username &&
      (connectionState.step === 'connecting' || connectionState.step === 'code' || connectionState.step === 'method')
    ) {
      return 'connecting';
    }
    // Check for connection error via WebSocket
    if (
      connectionState.username === acc.username &&
      connectionState.step === 'error'
    ) {
      return 'error';
    }
    // Check API connection_status field
    if (acc.connection_status === 'connecting') {
      return 'connecting';
    }
    if (acc.connection_status === 'error' || acc.connection_status === 'failed' || acc.connection_status === 'connection_issue') {
      return 'error';
    }
    // Check freeze_until for connection issues
    if (acc.freeze_until && new Date(acc.freeze_until) > new Date()) {
      return 'error';
    }
    // Check if account is locked
    if (acc.is_locked) {
      return 'locked';
    }
    // Active vs Paused
    return acc.is_active ? 'active' : 'paused';
  };

  const getStatusTag = (status: string) => {
    switch (status) {
      case 'active':
        return {
          label: t('accounts.active') || 'Active',
          bg: isDark ? '#064E3B' : '#ECFDF5',
          border: isDark ? '#10B981' : '#5EE9B5',
          color: isDark ? '#6EE7B7' : '#009966',
        };
      case 'paused':
        return {
          label: t('accounts.paused') || 'Paused',
          bg: isDark ? '#78350F' : '#FFFBEB',
          border: isDark ? '#F59E0B' : '#FFD230',
          color: isDark ? '#FCD34D' : '#E17100',
        };
      case 'connecting':
        return {
          label: t('accounts.connecting') || 'Connecting',
          bg: isDark ? '#1C398E' : '#EFF6FF',
          border: isDark ? '#155DFC' : '#8EC5FF',
          color: isDark ? '#8EC5FF' : '#155DFC',
        };
      case 'error':
        return {
          label: t('accounts.connectionIssue') || 'Connection issue',
          bg: isDark ? '#4C0519' : '#FFF1F2',
          border: isDark ? '#FB7185' : '#FFA1AD',
          color: isDark ? '#FDA4AF' : '#EC003F',
        };
      case 'locked':
        return {
          label: t('accounts.locked') || 'Locked',
          bg: isDark ? '#4C0519' : '#FFF1F2',
          border: isDark ? '#FB7185' : '#FFA1AD',
          color: isDark ? '#FDA4AF' : '#EC003F',
        };
      default:
        return {
          label: t('accounts.active') || 'Active',
          bg: isDark ? '#064E3B' : '#ECFDF5',
          border: isDark ? '#10B981' : '#5EE9B5',
          color: isDark ? '#6EE7B7' : '#009966',
        };
    }
  };

  const handleNavigateToAccountDetails = (id: string) => {
    // Pass state to indicate tutorial should start if coming from dashboard guide
    navigate(`/accounts/${id}/tabs`, {
      state: { startTutorial: showViewAccountGuide }
    });
  };

  if (loading) return <Spinner size="large" text={t('accounts.loading.accountData')} fullPage />;

  if (error) {
    return (
      <div>
        <div className="px-6 py-6">
          <div className="flex flex-col items-center justify-center py-20">
            <div className="text-red-500 text-2xl mb-2">
              <i className="bi bi-exclamation-triangle"></i>
            </div>
            <h3 className="text-base font-semibold text-gray-800 mb-1">{t('accounts.errors.errorLoadingAccounts')}</h3>
            <p className="text-xs text-gray-600 mb-3">{error}</p>
            <button
              className="px-4 py-2 bg-[#007bff] text-white rounded-md hover:bg-[#0069d9] transition-colors text-xs font-medium"
              onClick={() => window.location.reload()}
            >
              {t('common.retry')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!account || account.length === 0) return (
    <div
      className="flex flex-col items-start flex-1 min-h-0 font-geist p-0 lg:pt-2 lg:pr-2 lg:pb-2 lg:pl-0"
      style={{
        gap: '8px',
        background: isDark ? '#1D293D' : '#F8FAFC',
      }}
    >
      {/* Main Card */}
      <div
        className="flex flex-col items-start flex-1 w-full overflow-hidden rounded-none lg:rounded-2xl"
        style={{
          background: isDark ? '#0F172B' : '#FFFFFF',
          boxShadow: isDark
            ? '0px 1px 2px rgba(2, 6, 24, 0.5)'
            : '0px 1px 2px rgba(43, 127, 255, 0.1)',
        }}
      >
        {/* Header */}
        <div
          className="flex flex-row items-center w-full flex-shrink-0 rounded-none lg:rounded-t-2xl"
          style={{
            padding: '12px 16px',
            gap: '8px',
            borderBottom: `1px solid ${isDark ? '#1D293D' : '#F1F5F9'}`,
          }}
        >
          <button
            className="lg:hidden flex items-center justify-start flex-shrink-0"
            style={{ width: '24px', height: '36px', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
            onClick={toggleSidebar}
          >
            <Menu size={24} color={isDark ? '#E2E8F0' : '#314158'} />
          </button>
          <div
            className="hidden lg:flex items-center justify-center flex-shrink-0"
            style={{
              width: '36px',
              height: '36px',
              background: isDark ? '#1C398E' : '#EFF6FF',
              borderRadius: '12px',
            }}
          >
            <Instagram size={24} style={{ color: isDark ? '#51A2FF' : '#2B7FFF' }} />
          </div>
          <h1
            className="font-bold"
            style={{
              fontFamily: 'Geist, sans-serif',
              fontSize: '20px',
              lineHeight: '28px',
              color: isDark ? '#E2E8F0' : '#314158',
              margin: 0,
            }}
          >
            {t('accounts.pageTitle') || 'Accounts'}
          </h1>
        </div>

        {/* Content — centered empty state */}
        <div
          className="flex flex-row justify-center items-center flex-1 w-full"
          style={{ padding: '24px' }}
        >
          <div className="flex flex-col justify-center items-center gap-6 max-w-[480px] w-full">
            {/* Logomark */}
            <Logomark className="w-20 h-20" />

            {/* Title + Description */}
            <div className="flex flex-col items-center gap-4 w-full">
              <h2
                className="text-2xl font-bold leading-8 text-center w-full"
                style={{ color: isDark ? '#E2E8F0' : '#314158' }}
              >
                {t('accounts.emptyState.title') || "You're one step away from real growth"}
              </h2>
              <p
                className="text-base font-normal leading-6 text-center w-full"
                style={{ color: isDark ? '#E2E8F0' : '#314158' }}
              >
                {t('accounts.emptyState.description') || 'Connect an Instagram account to unlock automations, audience targeting, and live growth analytics'}
              </p>
            </div>

            {/* Gradient Button */}
            <button
              onClick={() => openModal()}
              className="w-full text-white text-base font-medium transition-opacity hover:opacity-90"
              style={{
                background: 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)',
                borderRadius: '12px',
                padding: '12px 16px',
                height: '48px',
              }}
            >
              {t('accounts.emptyState.connectButton') || 'Connect an Instagram account'}
            </button>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <AddAccountOverlay
          isOpen={isModalOpen}
          onClose={closeModal}
          reauthUsername={reauthUsername}
          accountData={(() => {
            const connecting = account?.find((a) => a.username === connectionState.username);
            return connecting ? { _id: connecting._id, username: connecting.username, name: connecting.name, profile_picture_base64: connecting.imgSrc } : null;
          })()}
          allAccounts={(account || []).map((a) => ({ _id: a._id, username: a.username, name: a.name, profile_picture_base64: a.imgSrc }))}
        />
      )}
    </div>
  );

  // Format large numbers (e.g., 2400 → "2.4k")
  const formatCount = (count: number | undefined) => {
    if (!count) return '0';
    if (count >= 1000000) return `${(count / 1000000).toFixed(1).replace(/\.0$/, '')}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1).replace(/\.0$/, '')}k`;
    return count.toString();
  };

  return (
    <div
      className="flex flex-col items-start flex-1 min-h-0 font-geist p-0 lg:pt-2 lg:pr-2 lg:pb-2 lg:pl-0"
      style={{
        gap: '8px',
        background: isDark ? '#1D293D' : '#F8FAFC',
      }}
    >
      {/* Main Card */}
      <div
        className="flex flex-col items-start flex-1 w-full overflow-hidden rounded-none lg:rounded-2xl"
        style={{
          background: isDark ? '#0F172B' : '#FFFFFF',
          boxShadow: isDark
            ? '0px 1px 2px rgba(2, 6, 24, 0.5)'
            : '0px 1px 2px rgba(43, 127, 255, 0.1)',
        }}
      >
        {/* Header */}
        <div
          className="flex flex-col justify-center items-start w-full flex-shrink-0 rounded-none lg:rounded-t-2xl"
          style={{
            padding: '12px 16px',
            borderBottom: `1px solid ${isDark ? '#1D293D' : '#F1F5F9'}`,
          }}
        >
          <div
            className="flex flex-row items-center w-full"
            style={{
              gap: '8px',
              height: '36px',
            }}
          >
            <button
              className="lg:hidden flex items-center justify-start flex-shrink-0"
              style={{ width: '24px', height: '36px', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
              onClick={toggleSidebar}
            >
              <Menu size={24} color={isDark ? '#E2E8F0' : '#314158'} />
            </button>
            <div
              className="hidden lg:flex items-center justify-center flex-shrink-0"
              style={{
                width: '36px',
                height: '36px',
                background: isDark ? '#1C398E' : '#EFF6FF',
                borderRadius: '12px',
              }}
            >
              <Instagram size={24} style={{ color: isDark ? '#51A2FF' : '#2B7FFF' }} />
            </div>
            <h1
              className="font-bold flex-1"
              style={{
                fontFamily: 'Geist, sans-serif',
                fontSize: '20px',
                lineHeight: '28px',
                color: isDark ? '#E2E8F0' : '#314158',
                margin: 0,
              }}
            >
              {t('accounts.pageTitle') || 'Accounts'}
            </h1>

            {/* Gradient Add Account button */}
            {subscription && (subscription.accountsPermitted ?? 0) > (account?.length || 0) && (
              <button
                onClick={() => openModal()}
                className="flex flex-row justify-center items-center flex-shrink-0 text-white transition-opacity hover:opacity-90"
                style={{
                  padding: '8px 12px',
                  gap: '4px',
                  height: '36px',
                  background: 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)',
                  borderRadius: '8px',
                }}
              >
                <Plus size={20} color="#FFFFFF" />
                <span
                  style={{
                    fontFamily: 'Geist, sans-serif',
                    fontWeight: 500,
                    fontSize: '14px',
                    lineHeight: '20px',
                    color: '#FFFFFF',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('accounts.addAccount') || 'Add account'}
                </span>
              </button>
            )}
          </div>
        </div>

        {/* Content — account cards */}
        <div
          className="relative flex-1 w-full"
          style={{ background: isDark ? '#0F172B' : '#FFFFFF' }}
        >
          <div
            className="absolute inset-0 overflow-y-auto"
            style={{ padding: '16px' }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {account && account.length > 0 && account
                .sort((a, b) => (a.is_active === b.is_active ? 0 : a.is_active ? -1 : 1))
                .map((acc, index: number) => {
                  const status = getAccountStatus(acc);
                  const tag = getStatusTag(status);

                  return (
                    <div
                      key={acc._id}
                      onClick={() => {
                        setShowViewAccountGuide(false);
                        handleNavigateToAccountDetails(acc._id);
                      }}
                      className="flex flex-col items-start cursor-pointer transition-shadow hover:shadow-md"
                      style={{
                        padding: '16px',
                        gap: '16px',
                        background: isDark ? '#0F172B' : '#FFFFFF',
                        border: `1px solid ${isDark ? '#314158' : '#E2E8F0'}`,
                        borderRadius: '8px',
                      }}
                    >
                      {/* Card Header: Avatar + Name + Tag */}
                      <div
                        className="flex flex-row items-start w-full"
                        style={{ gap: '8px', height: '24px' }}
                      >
                        {/* Picture + Name */}
                        <div
                          className="flex flex-row items-start flex-1 min-w-0"
                          style={{ gap: '4px', height: '24px' }}
                        >
                          {/* Avatar */}
                          <img
                            src={acc.profile_picture_base64 || AccountDefaultImage}
                            alt={acc.username}
                            className="flex-shrink-0 object-cover"
                            style={{
                              width: '24px',
                              height: '24px',
                              borderRadius: '10px',
                            }}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = AccountDefaultImage;
                            }}
                          />
                          {/* Name: @username */}
                          <div
                            className="flex flex-row items-center min-w-0"
                            style={{ height: '24px' }}
                          >
                            <span
                              className="flex-shrink-0"
                              style={{
                                fontFamily: 'Geist, sans-serif',
                                fontWeight: 700,
                                fontSize: '16px',
                                lineHeight: '24px',
                                color: isDark ? '#51A2FF' : '#2B7FFF',
                              }}
                            >
                              @
                            </span>
                            <span
                              className="truncate"
                              style={{
                                fontFamily: 'Geist, sans-serif',
                                fontWeight: 700,
                                fontSize: '16px',
                                lineHeight: '24px',
                                color: isDark ? '#E2E8F0' : '#314158',
                              }}
                            >
                              {acc.username}
                            </span>
                          </div>
                        </div>

                        {/* Status Tag */}
                        <div
                          className="flex flex-row items-center flex-shrink-0"
                          style={{ gap: '6px' }}
                        >
                          <div
                            className="flex flex-row justify-center items-center"
                            style={{
                              padding: status === 'active' ? '4px 8px' : '2px 6px',
                              background: tag.bg,
                              border: `1px solid ${tag.border}`,
                              borderRadius: '999px',
                              gap: '4px',
                            }}
                          >
                            {status === 'locked' && (
                              <Lock size={12} color={tag.color} />
                            )}
                            <span
                              style={{
                                fontFamily: 'Geist, sans-serif',
                                fontWeight: status === 'error' || status === 'locked' ? 400 : 500,
                                fontSize: '12px',
                                lineHeight: '16px',
                                textAlign: 'center',
                                color: tag.color,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {tag.label}
                            </span>
                          </div>

                          {/* Reconnect button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openModal(acc.username);
                            }}
                            title={t('accounts.reconnect') || 'Reconnect'}
                            className="flex items-center justify-center transition-opacity hover:opacity-80"
                            style={{
                              width: '28px',
                              height: '28px',
                              background: isDark ? '#1D293D' : '#F1F5F9',
                              borderRadius: '8px',
                              border: 'none',
                              cursor: 'pointer',
                            }}
                          >
                            <RotateCcw size={14} color={isDark ? '#90A1B9' : '#62748E'} />
                          </button>
                        </div>
                      </div>

                      {/* Stats Row */}
                      <div
                        className="flex flex-row items-start w-full"
                        style={{ gap: '16px' }}
                      >
                        {/* Followers */}
                        <div
                          className="flex flex-col items-start flex-1"
                          style={{
                            padding: '8px',
                            gap: '2px',
                            border: `1px solid ${isDark ? '#1D293D' : '#F1F5F9'}`,
                            borderRadius: '8px',
                          }}
                        >
                          <span
                            style={{
                              fontFamily: 'Geist, sans-serif',
                              fontWeight: 700,
                              fontSize: '16px',
                              lineHeight: '24px',
                              color: isDark ? '#E2E8F0' : '#314158',
                            }}
                          >
                            {formatCount(acc.followers_count)}
                          </span>
                          <span
                            style={{
                              fontFamily: 'Geist, sans-serif',
                              fontWeight: 400,
                              fontSize: '14px',
                              lineHeight: '20px',
                              color: isDark ? '#90A1B9' : '#62748E',
                            }}
                          >
                            {t('accounts.followers') || 'Followers'}
                          </span>
                        </div>

                        {/* Following */}
                        <div
                          className="flex flex-col items-start flex-1"
                          style={{
                            padding: '8px',
                            gap: '2px',
                            border: `1px solid ${isDark ? '#1D293D' : '#F1F5F9'}`,
                            borderRadius: '8px',
                          }}
                        >
                          <span
                            style={{
                              fontFamily: 'Geist, sans-serif',
                              fontWeight: 700,
                              fontSize: '16px',
                              lineHeight: '24px',
                              color: isDark ? '#E2E8F0' : '#314158',
                            }}
                          >
                            {formatCount(acc.following_count)}
                          </span>
                          <span
                            style={{
                              fontFamily: 'Geist, sans-serif',
                              fontWeight: 400,
                              fontSize: '14px',
                              lineHeight: '20px',
                              color: isDark ? '#90A1B9' : '#62748E',
                            }}
                          >
                            {t('accounts.following') || 'Following'}
                          </span>
                        </div>

                        {/* Posts */}
                        <div
                          className="flex flex-col items-start flex-1"
                          style={{
                            padding: '8px',
                            gap: '2px',
                            border: `1px solid ${isDark ? '#1D293D' : '#F1F5F9'}`,
                            borderRadius: '8px',
                          }}
                        >
                          <span
                            style={{
                              fontFamily: 'Geist, sans-serif',
                              fontWeight: 700,
                              fontSize: '16px',
                              lineHeight: '24px',
                              color: isDark ? '#E2E8F0' : '#314158',
                            }}
                          >
                            {formatCount(acc.media_count)}
                          </span>
                          <span
                            style={{
                              fontFamily: 'Geist, sans-serif',
                              fontWeight: 400,
                              fontSize: '14px',
                              lineHeight: '20px',
                              color: isDark ? '#90A1B9' : '#62748E',
                            }}
                          >
                            {t('accounts.posts') || 'Posts'}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <AddAccountOverlay
          isOpen={isModalOpen}
          onClose={closeModal}
          reauthUsername={reauthUsername}
          accountData={(() => {
            const connecting = account?.find((a) => a.username === connectionState.username);
            return connecting ? { _id: connecting._id, username: connecting.username, name: connecting.name, profile_picture_base64: connecting.imgSrc } : null;
          })()}
          allAccounts={(account || []).map((a) => ({ _id: a._id, username: a.username, name: a.name, profile_picture_base64: a.imgSrc }))}
        />
      )}
      <GuideTooltip
        targetRef={viewAccountButtonRef}
        message={t('accounts.guideTooltipMessage')}
        show={showViewAccountGuide}
      />
    </div>
  );
};

export default Accounts;
